/* eslint-disable react/sort-comp */
/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Chart from 'chart.js';

import {
	getChartData,
	getChildChartData,
	getChildScrChartData,
} from '../../utils/getChartData';

import './styles.scss';

// band fill (used if we can't read it from dataset)
const neutralBackgroundAreaColor = `rgba(255, 191, 0, 0.28)`;

// Matches the “Z-value” dataset regardless of language label
const Z_DATASET_LABELS = [
	'Z-skåre',
	'Z-Wert',
	'Z-Score',
	'Z-score',
	'Z value',
	'Z-Wert ',
];

const AVERAGE_AREA_LABELS = [
	'Gjennomsnittsbereich',
	'Gjennomsnittsområde',
	'Average area',
];

const AXIS_FONT_FAMILY =
	'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial';
const AXIS_LABEL_COLOR = 'rgba(17, 24, 39, 0.88)'; // darker
const AXIS_TICK_COLOR = 'rgba(0, 0, 1, 0.7)';

const isZDataset = (ds) =>
	ds &&
	typeof ds.label === 'string' &&
	Z_DATASET_LABELS.includes(ds.label.trim());

const isAverageAreaDataset = (ds) =>
	ds &&
	typeof ds.label === 'string' &&
	AVERAGE_AREA_LABELS.includes(ds.label.trim());

const safeNumber = (v) => {
	if (typeof v === 'number') return v;
	if (typeof v === 'string') {
		const n = Number(v);
		return Number.isFinite(n) ? n : null;
	}
	return null;
};

const toTransparent = (color) => {
	if (Array.isArray(color)) return color.map(toTransparent);
	if (typeof color !== 'string') return 'rgba(0,0,0,0)';

	const c = color.trim();

	// rgba(r,g,b,a) -> rgba(r,g,b,0)
	if (c.startsWith('rgba(')) {
		return c.replace(/rgba\(([^)]+)\)/, (m, inner) => {
			const parts = inner.split(',').map((p) => p.trim());
			if (parts.length < 3) return 'rgba(0,0,0,0)';
			return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, 0)`;
		});
	}

	// rgb(r,g,b) -> rgba(r,g,b,0)
	if (c.startsWith('rgb(')) {
		return c.replace(/rgb\(([^)]+)\)/, 'rgba($1, 0)');
	}

	// hex / named colors: just make it fully transparent
	return 'rgba(0,0,0,0)';
};

// Wrap helper: Chart.js v2 supports returning string[] for multi-line ticks
const wrapTick = (label, maxCharsPerLine = 22, maxLines = 2) => {
	const words = String(label).split(' ');
	const lines = [];
	let line = '';

	for (let i = 0; i < words.length; i++) {
		const test = line ? `${line} ${words[i]}` : words[i];
		if (test.length <= maxCharsPerLine) {
			line = test;
		} else {
			if (line) lines.push(line);
			line = words[i];
		}
	}
	if (line) lines.push(line);

	if (lines.length <= maxLines) return lines;
	return lines.slice(0, maxLines - 1).concat([`${lines[maxLines - 1]}…`]);
};

/**
 * Prettier markers
 * - dot inside average range
 * - X outside average range
 */
const drawCircle = (ctx, x, y, r, fillColor, strokeColor) => {
	ctx.save();

	ctx.shadowColor = 'rgba(0,0,0,0.12)';
	ctx.shadowBlur = 2;
	ctx.shadowOffsetX = 0;
	ctx.shadowOffsetY = 1;

	ctx.beginPath();
	ctx.arc(x, y, r, 0, Math.PI * 2);
	ctx.fillStyle = fillColor || '#9fd4d7';
	ctx.fill();

	ctx.shadowColor = 'transparent';
	ctx.lineWidth = 2;
	ctx.strokeStyle = strokeColor || 'rgba(0,0,0,0.45)';
	ctx.stroke();

	ctx.restore();
};

const drawCross = (ctx, x, y, size, strokeColor) => {
	ctx.save();

	ctx.lineCap = 'round';
	ctx.lineJoin = 'round';

	ctx.shadowColor = 'rgba(0,0,0,0.12)';
	ctx.shadowBlur = 2;
	ctx.shadowOffsetX = 0;
	ctx.shadowOffsetY = 1;

	// OUTLINE pass (white)
	ctx.strokeStyle = 'rgba(255,255,255,0.95)';
	ctx.lineWidth = 6;
	ctx.beginPath();
	ctx.moveTo(x - size, y - size);
	ctx.lineTo(x + size, y + size);
	ctx.moveTo(x - size, y + size);
	ctx.lineTo(x + size, y - size);
	ctx.stroke();

	// MAIN pass
	ctx.shadowColor = 'transparent';
	ctx.strokeStyle = strokeColor || '#0f4256';
	ctx.lineWidth = 3.5;
	ctx.beginPath();
	ctx.moveTo(x - size, y - size);
	ctx.lineTo(x + size, y + size);
	ctx.moveTo(x - size, y + size);
	ctx.lineTo(x + size, y - size);
	ctx.stroke();

	ctx.restore();
};

/**
 * Plugin: draw the average band perfectly aligned to x = [-1, 1]
 * (no leak, no extra canvas)
 */
const createAverageBandPlugin = () => ({
	beforeDatasetsDraw: (chart) => {
		const ctx = chart.chart && chart.chart.ctx;
		if (!ctx) return;

		const xScale =
			chart.scales['x-axis-0'] ||
			Object.values(chart.scales).find(
				(s) => s && s.isHorizontal && s.isHorizontal()
			);

		if (!xScale) return;

		const area = chart.chartArea;
		if (!area) return;

		const avgDs = chart.data.datasets.find(isAverageAreaDataset);
		const bandColor =
			(avgDs &&
				(Array.isArray(avgDs.backgroundColor)
					? avgDs.backgroundColor[0]
					: avgDs.backgroundColor)) ||
			neutralBackgroundAreaColor;

		const xLeft = xScale.getPixelForValue(-1);
		const xRight = xScale.getPixelForValue(1);

		const left = Math.min(xLeft, xRight);
		const width = Math.abs(xRight - xLeft);
		const height = area.bottom - area.top;

		ctx.save();
		ctx.fillStyle = bandColor;
		ctx.fillRect(left, area.top, width, height);

		ctx.strokeStyle = 'rgba(197, 24, 24, 0.12)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(xLeft, area.top);
		ctx.lineTo(xLeft, area.bottom);
		ctx.moveTo(xRight, area.top);
		ctx.lineTo(xRight, area.bottom);
		ctx.stroke();

		ctx.restore();
	},
});

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const createZMarkerPlugin = () => ({
	afterDatasetsDraw: (chart) => {
		const zIndex = chart.data.datasets.findIndex(isZDataset);
		if (zIndex < 0) return;

		const ds = chart.data.datasets[zIndex];
		const meta = chart.getDatasetMeta(zIndex);
		if (!meta || meta.hidden) return;

		const ctx = chart.chart.ctx;
		const area = chart.chartArea;
		if (!area) return;

		ctx.save();

		const size = 9;
		// size + a bit extra because you draw a thick white outline too
		const margin = size + 6;

		meta.data.forEach((barEl, i) => {
			const model = barEl && barEl._model;
			if (!model) return;

			// Base x/y from chart element
			let x = model.x;
			let y = model.y;

			x = clamp(x, area.left + margin, area.right - margin);
			y = clamp(y, area.top + margin, area.bottom - margin);

			const strokeColor =
				(ds._markerStrokeColors && ds._markerStrokeColors[i]) ||
				'#03151d';

			drawCross(ctx, x, y, size, strokeColor);
		});

		ctx.restore();
	},
});

class HorizontalBarChart extends React.Component {
	constructor() {
		super();

		this.state = {
			showTestUserInfo: false,
			testResult: null,
		};
	}

	prepareDataForMarkers = (data) => {
		if (!data || !Array.isArray(data.datasets)) return data;

		const next = {
			...data,
			labels: Array.isArray(data.labels) ? [...data.labels] : data.labels,
			datasets: data.datasets.map((ds) => ({...ds})),
		};

		// 1) Make Z bars invisible, but keep them for tooltip/positioning
		const zIndex = next.datasets.findIndex(isZDataset);
		if (zIndex >= 0) {
			const zDs = next.datasets[zIndex];

			const bg = zDs.backgroundColor;
			const br = zDs.borderColor;
			const len = Array.isArray(zDs.data) ? zDs.data.length : 0;

			zDs._markerFillColors = Array.isArray(bg)
				? bg.slice()
				: typeof bg === 'string'
					? Array.from({length: len}, () => bg)
					: Array.from({length: len}, () => '#9fd4d7');

			zDs._markerStrokeColors = Array.isArray(br)
				? br.slice()
				: typeof br === 'string'
					? Array.from({length: len}, () => br)
					: Array.from({length: len}, () => '#0f4256');

			zDs.backgroundColor = toTransparent(zDs.backgroundColor);
			zDs.hoverBackgroundColor = toTransparent(
				zDs.hoverBackgroundColor || zDs.backgroundColor
			);
			zDs.borderColor = toTransparent(zDs.borderColor);
			zDs.hoverBorderColor = toTransparent(
				zDs.hoverBorderColor || zDs.borderColor
			);
			zDs.borderWidth = 0;
			zDs.hoverBorderWidth = 0;
		}

		// 2) Prevent Chart.js from drawing the old average-area dataset
		const avgIndex = next.datasets.findIndex(isAverageAreaDataset);
		if (avgIndex >= 0) {
			const avgDs = next.datasets[avgIndex];
			avgDs.data = [];
			avgDs.borderWidth = 0;
			avgDs.pointRadius = 0;
			avgDs.pointHoverRadius = 0;
		}

		return next;
	};

	componentDidMount() {
		const {subtests, isChild, testType} = this.props;

		const chartDataRaw = isChild
			? getChildChartData(subtests, false)
			: testType === 'child-screening'
				? getChildScrChartData(subtests, false)
				: getChartData(subtests, false);

		const printChartDataRaw = isChild
			? getChildChartData(subtests, true)
			: testType === 'child-screening'
				? getChildScrChartData(subtests, false)
				: getChartData(subtests, true);

		const chartData = this.prepareDataForMarkers(chartDataRaw);
		const printChartData = this.prepareDataForMarkers(printChartDataRaw);

		const el = ReactDOM.findDOMNode(this.visible);
		const ctx = el.getContext('2d');

		this.chart = this.renderChart(ctx, chartData);

		const printElem = ReactDOM.findDOMNode(this.printable);
		const printCtx = printElem.getContext('2d');
		this.printChart = this.renderChart(printCtx, printChartData);
	}

	componentWillUnmount() {
		if (this.chart) this.chart.destroy();
		if (this.printChart) this.printChart.destroy();
	}

	renderChart(ctx, data) {
		const chart = new Chart(ctx, {
			type: 'horizontalBar',
			data,
			plugins: [createAverageBandPlugin(), createZMarkerPlugin()],
			options: {
				responsive: true,
				maintainAspectRatio: false,
				layout: {
					padding: {
						left: 16,
						right: 12,
						top: 8,
						bottom: 24,
					},
				},
				scales: {
					xAxes: [
						{
							ticks: {
								max: 4,
								min: -4,
								stepSize: 1,
								fontFamily: AXIS_FONT_FAMILY,
								fontSize: 13,
								fontColor: AXIS_TICK_COLOR,
								padding: 10,
							},
							gridLines: {
								color: 'rgba(0, 0, 0, 0.08)',
								zeroLineColor: 'rgba(0,0,0,0.18)',
								drawBorder: false,
							},
						},
					],
					yAxes: [
						{
							ticks: {
								fontFamily: AXIS_FONT_FAMILY,
								fontSize: 12,
								fontStyle: '500',
								fontColor: AXIS_LABEL_COLOR,
								padding: 10,
								callback: (v) => wrapTick(v, 22, 2), // <--- WRAP + nicer
							},
							gridLines: {
								color: 'rgba(0,0,0,0.06)',
								drawBorder: false,
							},
						},
					],
				},
				legend: {
					labels: {
						filter: (legendItem) => {
							if (legendItem.text !== 'Z-skåre')
								return legendItem;
							return null;
						},
						fontFamily: AXIS_FONT_FAMILY,
						fontSize: 13,
						fontColor: 'rgba(0,0,0,0.65)',
						padding: 16,
					},
				},
				tooltips: {
					mode: 'nearest',
					intersect: true,
					yAlign: 'bottom',

					callbacks: {
						label: (tooltipItem) =>
							`Z-Wert: ${Number(tooltipItem.xLabel).toFixed(2)}`,
					},
				},
			},
		});

		return chart;
	}

	render() {
		return (
			<div style={{position: 'relative', height: 420}}>
				<canvas
					className="non-printable"
					ref={(node) => (this.visible = node)}
				/>
				<canvas
					className="printable"
					ref={(node) => (this.printable = node)}
				/>
			</div>
		);
	}
}

HorizontalBarChart.defaultProps = {
	isChild: false,
};

HorizontalBarChart.propTypes = {
	isChild: PropTypes.bool,
	subtests: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default HorizontalBarChart;
